import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Grid,
  TextField,
  Typography,
  Avatar,
} from '@mui/material';
import Button from '@mui/material/Button';
import MDButton from 'components/MDButton';
import * as yup from 'yup';
import Swal from 'sweetalert2';
import api from 'core/interceptors/api';
import { useFormik } from 'formik';

const validationAppCategorySchema = yup.object().shape({
  app_category: yup.string()
    .required('App Category is required')
    .matches(/^[A-Za-z\s]+$/, 'App Category can only contain letters and spaces'),
});

export default function AppCategoryEditModel({ open, handleClose, editInput }) {
  const [duplicateError, setDuplicateError] = useState('');
  const [image, setImage] = useState(null);
  const [imageError, setImageError] = useState('');
  const [preview, setPreview] = useState(null);
  const [clearExistingImage, setClearExistingImage] = useState(false);

  useEffect(() => {
    if (editInput?.image_url) {
      setPreview(editInput.image_url);
    } else {
      setPreview(null);
    }
    setImage(null);
    setImageError('');
    setDuplicateError('');
    setClearExistingImage(false);
  }, [editInput, open]);

  const initialValues = {
    app_category: editInput?.app_category_name || '',
    app_category_id: editInput?.app_category_id || '',
  };

  const checkDuplicate = async (value) => {
    const trimmed = value.trim();
    if (!trimmed || trimmed === editInput?.app_category_name || !/^[A-Za-z\s]+$/.test(trimmed)) {
      setDuplicateError('');
      return;
    }

    try {
      const res = await api.post('/app_management/app_category/check_duplicate', {
        app_category: trimmed,
      });
      if (res.data.status === 1 && res.data.app_category_id !== editInput.app_category_id) {
        setDuplicateError('App Category already exists');
      } else {
        setDuplicateError('');
      }
    } catch (error) {
      console.error('Error checking duplicate:', error);
      setDuplicateError('Error checking duplicate');
    }
  };

  const handleImageChange = (e) => {
    const file = e.target.files[0];
    if (file && file.size > 2 * 1024 * 1024) {
      setImageError('Image size must be less than 2MB');
      setImage(null);
      setPreview(editInput?.image_url || null);
      setClearExistingImage(false);
    } else if (file) {
      setImageError('');
      setImage(file);
      setPreview(URL.createObjectURL(file));
      setClearExistingImage(false); 
    } else {
      setImage(null);
      setImageError('');
      setPreview(editInput?.image_url && !clearExistingImage ? editInput.image_url : null);
    }
  };

  const handleClearImage = () => {
    setImage(null);
    setPreview(null);
    setImageError('');
    setClearExistingImage(true); 
  };

  const formik = useFormik({
    initialValues,
    validationSchema: validationAppCategorySchema,
    enableReinitialize: true,
    onSubmit: async (values) => {
      await formik.validateForm();
      if (!formik.isValid || duplicateError || imageError) {
        return;
      }

      try {
        const formData = new FormData();
        formData.append('app_category', values.app_category.trim());

        if (image) {
          formData.append('image', image);
        } else if (clearExistingImage) {
          formData.append('clear_image', 'true');
        }

        const response = await api.put(
          `/app_management/app_category/${values.app_category_id}`,
          formData,
          {
            headers: {
              'Content-Type': 'multipart/form-data',
            },
          }
        );

        if (response.data.status === 1) {
          Swal.fire({
            icon: 'success',
            title: 'App Category updated successfully!',
            timer: 3000,
            showConfirmButton: false,
          });
          formik.resetForm();
          setImage(null);
          setPreview(null);
          setImageError('');
          setDuplicateError('');
          setClearExistingImage(false);
          handleClose();
        } else {
          Swal.fire({
            icon: 'error',
            title: 'Failed',
            text: response.data.msg,
          });
        }
      } catch (error) {
        console.error('Error updating app category:', error);
        Swal.fire({
          icon: 'error',
          title: 'Error',
          text: 'Failed to update app category.',
        });
      }
    },
  });

  const handleCancel = () => {
    formik.resetForm();
    formik.setErrors({});
    formik.setTouched({});
    setImage(null);
    setImageError('');
    setPreview(editInput?.image_url || null); 
    setDuplicateError('');
    setClearExistingImage(false);
    handleClose();
  };

  return (
    <Dialog open={open} onClose={handleCancel} fullWidth maxWidth="md">
      <DialogTitle>Edit App Category</DialogTitle>
      <form onSubmit={formik.handleSubmit}>
        <DialogContent>
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <TextField
                label="App Category"
                name="app_category"
                value={formik.values.app_category}
                onChange={async (e) => { 
                  formik.handleChange(e);
                  formik.setFieldTouched('app_category', true, false); 
                  await formik.validateField('app_category'); 

                  if (validationAppCategorySchema.fields.app_category.isValidSync(e.target.value)) {
                    checkDuplicate(e.target.value);
                  } else {
                    setDuplicateError(''); 
                  }
                }}
                onBlur={formik.handleBlur} 
                fullWidth
                margin="normal"
                error={
                  (formik.touched.app_category && Boolean(formik.errors.app_category)) ||
                  Boolean(duplicateError)
                }
                helperText={
                  (formik.touched.app_category && formik.errors.app_category) ||
                  duplicateError
                }
              />
            </Grid>

            <Grid item xs={12}>
              <MDButton
                variant="outlined"
                color="info"
                component="label"
                fullWidth
                sx={{ justifyContent: 'center' }}
              >
                Upload Image (Max 2MB)
                <input
                  hidden
                  accept="image/*"
                  type="file"
                  onChange={handleImageChange}
                  onClick={(event) => {
                    event.target.value = null;
                  }}
                />
              </MDButton>

            
              {(preview) && ( 
                <Grid container alignItems="center" spacing={2} sx={{ mt: 2 }}>
                  <Grid item>
                    <Avatar
                      src={preview}
                      variant="rounded"
                      sx={{ width: 100, height: 100 }}
                    />
                  </Grid>
                  
                </Grid>
              )}

              {imageError && (
                <Typography variant="body2" color="error" sx={{ mt: 1 }}>
                  {imageError}
                </Typography>
              )}
            </Grid>
          </Grid>
        </DialogContent>

        <DialogActions>
          <Button onClick={handleCancel} color="primary">
            Cancel
          </Button>
          <MDButton
            type="submit"
            color="success"
            disabled={!formik.isValid || Boolean(duplicateError) || Boolean(imageError)}
          >
            Save
          </MDButton>
        </DialogActions>
      </form>
    </Dialog>
  );
}